import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChallengeHero from '../components/domain/ChallengeHero';
import ProgressArc from '../components/domain/ProgressArc';
import api from '../api/client';

function parseJsonField(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

/**
 * CampaignPreview — podgląd widoku pracownika (sekcja 16.5).
 * HR widzi jak kampania wygląda z perspektywy uczestnika.
 */
function CampaignPreview() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/campaigns/${id}`)
      .then((data) => {
        if (data && !data.error) setCampaign(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const charity = parseJsonField(campaign?.charity, {});
  const title = campaign?.title || 'Razem do 10 milionów kroków';
  const PREVIEW_STATUS_LABELS = {
    active: 'Aktywna',
    paused: 'Wstrzymana',
    completed: 'Zakończona',
    scheduled: 'Zaplanowana',
    registration_open: 'W trakcie zapisów',
    wind_down: 'Rozliczenie',
    draft: 'Draft',
    cancelled: 'Odwołana',
  };
  const statusLabel = PREVIEW_STATUS_LABELS[campaign?.status] || 'Aktywna';
  const progress = charity.currentAmount && charity.goalAmount ? Math.round((charity.currentAmount / charity.goalAmount) * 100) : 84;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/campaigns/${id}`)}
          sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
        >
          Wróć do edycji
        </Button>
        <Chip label="Podgląd pracownika" variant="outlined" size="small" />
      </Box>

      {/* Phone frame */}
      <Box
        sx={{
          maxWidth: 375,
          mx: 'auto',
          border: `2px solid ${theme.palette.divider}`,
          borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px`,
          overflow: 'hidden',
          bgcolor: theme.tokens?.semantic?.surface?.canvas ?? '#F7F6F3',
          boxShadow: theme.tokens?.semantic?.shadow?.float,
        }}
      >
        {/* Simulated status bar */}
        <Box sx={{ height: 44, bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', px: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            09:41
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">●●● Wi-Fi 🔋</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2.5 }}>
          {/* Day context */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Wtorek, 20 maja 2025
          </Typography>

          {/* Hero card */}
          <Box
            sx={{
              borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px`,
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              p: 2.5,
              mb: 3,
            }}
          >
            <Chip label={statusLabel} color={statusLabel === 'Aktywna' ? 'success' : 'default'} size="small" sx={{ mb: 1, fontWeight: 700, fontSize: '0.625rem' }} />
            <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, mb: 2, fontSize: '1.1rem' }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1, fontSize: '1.75rem' }}>
                  8 420
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  kroków dzisiaj
                </Typography>
              </Box>
              <ProgressArc value={84} label="84%" sublabel="celu" size={56} />
            </Box>
            <Button variant="contained" fullWidth size="small" sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}>
              Synchronizuj wynik
            </Button>
          </Box>

          {/* Metric strip */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                74%
              </Typography>
              <Typography variant="caption" color="text.secondary">celu</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                #4
              </Typography>
              <Typography variant="caption" color="text.secondary">w firmie</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                12 dni
              </Typography>
              <Typography variant="caption" color="text.secondary">zostało</Typography>
            </Box>
          </Box>

          {/* Team goal */}
          <Box
            sx={{
              p: 2,
              borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              mb: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Cel zespołowy
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: theme.palette.action.selected,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ height: '100%', width: '74%', bgcolor: theme.tokens?.semantic?.progress?.fill ?? 'primary.main', borderRadius: 4, transition: 'width 420ms ease' }} />
                </Box>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>7.4M / 10M</Typography>
            </Box>
          </Box>

          {/* Last activity */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Ostatnia aktywność
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption">
              <strong>Anna K.</strong> osiągnęła cel dzienny — 15 min temu
            </Typography>
            <Typography variant="caption">
              <strong>Zespół Marketing</strong> awansował na #2 — 1h temu
            </Typography>
          </Box>
        </Box>

        {/* Simulated bottom nav */}
        <Box
          sx={{
            height: 56,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            px: 2,
          }}
        >
          {['Dzisiaj', 'Odkrywaj', 'Społeczność', 'Profil'].map((label) => (
            <Typography key={label} variant="caption" sx={{ fontWeight: label === 'Dzisiaj' ? 700 : 400, color: label === 'Dzisiaj' ? 'primary.main' : 'text.secondary', fontSize: '0.625rem' }}>
              {label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default CampaignPreview;
